import { useEffect, useState } from "react";
import {
  App as AntdApp,
  Alert,
  Button,
  Card,
  Empty,
  Form as AntForm,
  Input,
  Modal,
  Space,
  Table,
  Tag,
  Typography,
} from "antd";
import { PlusOutlined, ReloadOutlined } from "@ant-design/icons";
import type { ColumnsType } from "antd/es/table";
import { FormProvider, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { authFetch } from "../../utils/api";
import {
  canMutateSharedInfra,
  SHARED_INFRA_READONLY_MESSAGE,
} from "../../utils/permissions";
import {
  DangerousActionModal,
  FormActions,
  FormField,
} from "../../shared/form";
import {
  AddSyncAllowedIpSchema,
  type AddSyncAllowedIpValues,
} from "../../schemas/singboxSyncAllowlist";

interface AllowedIp {
  id: number;
  ip: string;
  notes: string | null;
  source: "admin" | "migration";
  createdByAdminName: string | null;
  createdAt: string;
}

interface AllowlistSnapshot {
  items: AllowedIp[];
  total: number;
  compatibility: { source: "env" | "default"; ips: string[] };
  environment: string;
  canAdd: boolean;
  canDelete: boolean;
}

const emptyValues: AddSyncAllowedIpValues = { ip: "", notes: "" };
const endpoint = "/api/admin/sing-box-sync/allowlist";

const columns: ColumnsType<AllowedIp> = [
  {
    title: "允許的來源 IP",
    dataIndex: "ip",
    width: 240,
    render: (ip: string) => (
      <Typography.Text copyable code>
        {ip}
      </Typography.Text>
    ),
  },
  {
    title: "備註",
    dataIndex: "notes",
    width: 240,
    render: (notes: string | null) => notes || "—",
  },
  {
    title: "來源",
    dataIndex: "source",
    width: 120,
    render: (source: AllowedIp["source"]) => (
      <Tag color={source === "admin" ? "blue" : "default"}>
        {source === "admin" ? "管理員新增" : "部署匯入"}
      </Tag>
    ),
  },
  {
    title: "新增者",
    dataIndex: "createdByAdminName",
    width: 150,
    render: (name: string | null) => name || "部署匯入",
  },
  {
    title: "新增時間",
    dataIndex: "createdAt",
    width: 190,
    render: (date: string) => new Date(date).toLocaleString(),
  },
];

export const SingboxSyncAllowlistPage: React.FC = () => {
  const { message } = AntdApp.useApp();
  const [snapshot, setSnapshot] = useState<AllowlistSnapshot | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [query, setQuery] = useState({ page: 1, pageSize: 20, ip: "" });
  const [search, setSearch] = useState("");
  const [revision, setRevision] = useState(0);
  const [open, setOpen] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [deleteTarget, setDeleteTarget] = useState<AllowedIp | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState("");
  const form = useForm<AddSyncAllowedIpValues>({
    resolver: zodResolver(AddSyncAllowedIpSchema),
    mode: "onBlur",
    defaultValues: emptyValues,
  });
  const canAdd =
    !loading && !error && snapshot?.canAdd === true && canMutateSharedInfra();

  const canDelete =
    !loading &&
    !error &&
    snapshot?.canDelete === true &&
    canMutateSharedInfra();
  const targetHasCompatibility =
    !!deleteTarget &&
    snapshot?.compatibility.ips.includes(deleteTarget.ip) === true;

  useEffect(() => {
    const abort = new AbortController();
    setLoading(true);
    setError("");
    const load = async () => {
      try {
        const params = new URLSearchParams({
          page: String(query.page),
          pageSize: String(query.pageSize),
        });
        if (query.ip) params.set("ip", query.ip);
        const response = await authFetch(`${endpoint}?${params}`, {
          signal: abort.signal,
        });
        const result = await response.json();
        if (!response.ok || result.code !== 0)
          throw new Error(result.msg || "白名單載入失敗");
        if (!abort.signal.aborted) setSnapshot(result.data);
      } catch (err) {
        if (!abort.signal.aborted)
          setError(
            err instanceof Error ? err.message : "白名單載入失敗，請重試",
          );
      } finally {
        if (!abort.signal.aborted) setLoading(false);
      }
    };
    void load();
    return () => abort.abort();
  }, [query, revision]);

  const close = () => {
    if (form.formState.isSubmitting) return;
    setOpen(false);
    setSubmitError("");
    form.reset(emptyValues);
  };

  const add = async (values: AddSyncAllowedIpValues) => {
    if (!canAdd) {
      setSubmitError(SHARED_INFRA_READONLY_MESSAGE);
      return;
    }
    setSubmitError("");
    try {
      const response = await authFetch(endpoint, {
        method: "POST",
        body: JSON.stringify(values),
      });
      const result = await response.json();
      if (!response.ok || result.code !== 0) {
        const msg = result.msg || "新增失敗，請重試";
        if (result.data?.field === "ip" || result.data?.field === "notes") {
          form.setError(result.data.field, { message: msg });
        }
        if (response.status === 403) {
          setSnapshot((current) =>
            current ? { ...current, canAdd: false, canDelete: false } : current,
          );
        }
        throw new Error(msg);
      }
      message.success(
        result.data.restored
          ? "已重新啟用原紀錄，保留原備註與新增者，立即生效"
          : result.data.created
            ? "已新增，立即生效，無須重啟 BO"
            : "此 IP 已在管理清單中",
      );
      setOpen(false);
      form.reset(emptyValues);
      setSearch("");
      setQuery((current) => ({ ...current, page: 1, ip: "" }));
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : "新增失敗，請重試");
    }
  };

  const remove = async () => {
    if (!deleteTarget || deleting) return;
    if (!canDelete) {
      setDeleteError(SHARED_INFRA_READONLY_MESSAGE);
      return;
    }
    setDeleting(true);
    setDeleteError("");
    try {
      const response = await authFetch(`${endpoint}/${deleteTarget.id}`, {
        method: "DELETE",
      });
      const result = await response.json();
      if (!response.ok || result.code !== 0) {
        if (response.status === 403) {
          setSnapshot((current) =>
            current ? { ...current, canAdd: false, canDelete: false } : current,
          );
        }
        throw new Error(result.msg || "刪除失敗，請重試");
      }
      if (result.data.compatibilityAllowed) {
        message.warning("已移除 DB 管理紀錄；此 IP 仍由既有部署清單授權");
      } else {
        message.success(
          result.data.deleted
            ? "已刪除，立即生效，無須重啟 BO"
            : "此 DB 管理紀錄已不存在",
        );
      }
      setDeleteTarget(null);
      setQuery((current) => ({
        ...current,
        page:
          snapshot?.items.length === 1 && current.page > 1
            ? current.page - 1
            : current.page,
      }));
    } catch (err) {
      setDeleteError(err instanceof Error ? err.message : "刪除失敗，請重試");
    } finally {
      setDeleting(false);
    }
  };

  const tableColumns: ColumnsType<AllowedIp> = canDelete
    ? [
        ...columns,
        {
          title: "操作",
          key: "actions",
          width: 140,
          render: (_, entry) =>
            entry.source === "admin" ? (
              <Button
                danger
                aria-label={`刪除 ${entry.ip}`}
                disabled={deleting}
                onClick={() => {
                  setDeleteError("");
                  setDeleteTarget(entry);
                }}
              >
                刪除
              </Button>
            ) : (
              <Typography.Text type="secondary">匯入紀錄唯讀</Typography.Text>
            ),
        },
      ]
    : columns;

  return (
    <Space direction="vertical" size="large" style={{ width: "100%" }}>
      <div>
        <Space wrap>
          <Typography.Title level={3} style={{ margin: 0 }}>
            節點同步 IP 白名單
          </Typography.Title>
          {snapshot && <Tag>{snapshot.environment.toUpperCase()}</Tag>}
        </Space>
        <Typography.Paragraph
          type="secondary"
          style={{ marginTop: 8, marginBottom: 0 }}
        >
          管理允許向 BO 拉取節點設定的來源 IP。新增後立即生效，無須重啟
          BO；節點仍須使用有效的同步憑證。僅能刪除透過 API／Web 新增的紀錄。
        </Typography.Paragraph>
      </div>
      {error && (
        <Alert
          type="error"
          showIcon
          message={error}
          action={
            <Button aria-label="重試" onClick={() => setRevision((v) => v + 1)}>
              重試
            </Button>
          }
        />
      )}
      {!loading && !error && snapshot && !snapshot.canAdd && (
        <Alert type="info" showIcon message={SHARED_INFRA_READONLY_MESSAGE} />
      )}
      <Card
        title="管理清單"
        extra={
          <Space wrap>
            <Button
              icon={<ReloadOutlined aria-hidden />}
              loading={loading}
              onClick={() => setRevision((v) => v + 1)}
            >
              重新整理
            </Button>
            {canAdd && (
              <Button
                type="primary"
                aria-label="新增 IP"
                icon={<PlusOutlined aria-hidden />}
                onClick={() => {
                  form.reset(emptyValues);
                  setSubmitError("");
                  setOpen(true);
                }}
              >
                新增 IP
              </Button>
            )}
          </Space>
        }
      >
        <Input.Search
          aria-label="搜尋來源 IP"
          placeholder="輸入完整 IP 查詢"
          value={search}
          allowClear
          onChange={(event) => setSearch(event.target.value)}
          onSearch={(ip) =>
            setQuery((current) => ({ ...current, page: 1, ip: ip.trim() }))
          }
          style={{ width: "100%", maxWidth: 400, marginBottom: 16 }}
        />
        <Table<AllowedIp>
          rowKey="id"
          columns={tableColumns}
          dataSource={error ? [] : snapshot?.items || []}
          loading={loading}
          scroll={{ x: canDelete ? 1080 : 940 }}
          locale={{
            emptyText: error ? (
              "資料載入失敗，請重試"
            ) : (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={
                  query.ip
                    ? "找不到此 IP；也請確認下方既有部署清單"
                    : "尚未新增 IP，可使用「新增 IP」加入節點來源位址"
                }
              />
            ),
          }}
          pagination={{
            current: query.page,
            pageSize: query.pageSize,
            total: error ? 0 : snapshot?.total || 0,
            showSizeChanger: true,
            pageSizeOptions: [20, 50, 100],
            showTotal: (total) => `共 ${total} 筆`,
            onChange: (page, pageSize) =>
              setQuery((current) => ({ ...current, page, pageSize })),
          }}
        />
      </Card>
      {!error && snapshot && (
        <Card title="既有部署清單（持續有效）">
          <Typography.Paragraph type="secondary">
            以下來源 IP 保留原有授權，與管理清單共同生效。新節點請使用上方「新增
            IP」。此區為唯讀，刪除 DB 紀錄不會移除以下授權。
          </Typography.Paragraph>
          <Space wrap>
            {snapshot.compatibility.ips.map((ip) => (
              <Tag key={ip}>
                <Typography.Text copyable>{ip}</Typography.Text>
              </Tag>
            ))}
          </Space>
          {snapshot.compatibility.ips.length === 0 && (
            <Typography.Text type="secondary">
              沒有有效的既有部署 IP
            </Typography.Text>
          )}
        </Card>
      )}
      <DangerousActionModal
        open={!!deleteTarget}
        title="刪除同步來源 IP"
        confirmKeyword={deleteTarget?.ip || ""}
        confirmLabel="確認刪除"
        confirming={deleting}
        isExtraValid={canDelete}
        warning={
          targetHasCompatibility
            ? "此 IP 也在既有部署清單中。刪除僅移除 DB 管理紀錄，來源 IP 仍會被允許拉取設定。"
            : "刪除後此 IP 將無法再透過 DB 白名單拉取節點設定，下一次請求立即生效。"
        }
        extra={
          deleteError ? (
            <Alert type="error" showIcon message={deleteError} />
          ) : undefined
        }
        onConfirm={() => void remove()}
        onCancel={() => {
          if (deleting) return;
          setDeleteTarget(null);
          setDeleteError("");
        }}
      />
      <Modal
        title="新增同步來源 IP"
        open={open}
        onCancel={close}
        footer={null}
        destroyOnHidden
        width={480}
        maskClosable={!form.formState.isSubmitting}
        closable={!form.formState.isSubmitting}
      >
        <Typography.Paragraph type="secondary">
          請填入 BO 實際看到的節點出口 IP；經 NAT 的節點可能與節點資料中的 IP
          不同。
        </Typography.Paragraph>
        {submitError && (
          <Alert
            type="error"
            showIcon
            message={submitError}
            style={{ marginBottom: 16 }}
          />
        )}
        <FormProvider {...form}>
          <AntForm
            layout="vertical"
            onFinish={form.handleSubmit(add)}
            disabled={form.formState.isSubmitting}
          >
            <FormField<AddSyncAllowedIpValues>
              name="ip"
              label="來源 IP"
              required
              help="IPv4 或 IPv6 單一位址，不含網段或埠"
              control={
                <Input
                  aria-label="來源 IP"
                  placeholder="例如 203.0.113.10"
                  autoComplete="off"
                />
              }
            />
            <FormField<AddSyncAllowedIpValues>
              name="notes"
              label="備註"
              help="選填，最多 255 字"
              control={
                <Input.TextArea
                  aria-label="備註"
                  placeholder="例如節點名稱、用途"
                  rows={3}
                  maxLength={255}
                  showCount
                />
              }
            />
            <FormActions
              onCancel={close}
              submitting={form.formState.isSubmitting}
              dirty={form.formState.isDirty}
              submitLabel="新增並立即生效"
            />
          </AntForm>
        </FormProvider>
      </Modal>
    </Space>
  );
};
